// Authed review-tool reads — comments + annotations on a candidate
// (PRD §9.7 + B4 PR5b / B4e2).
//
// Counterparts to `useShareComments` / `useShareAnnotations` from
// queries/share.ts. Same response shapes (`CommentOut`, `AnnotationOut`)
// because the backend serializes both surfaces through the same
// schemas — only the auth model and the URL path differ. Cache namespace
// is the `(orgSlug, projectId, candidateId, …)` tuple so authed reads
// never collide with share-viewer reads against the same candidate.
//
// Comments are scoped to the candidate, not to a frame; annotations
// are scoped to `(candidate, frame)` because the backend filters
// server-side and switching frames must swap the annotation set
// (PRD §9.5 reading 5).

import { skipToken, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { client } from '../client';
import type { components } from '../schema';

export type CommentOut = components['schemas']['CommentOut'];
export type AnnotationOut = components['schemas']['AnnotationOut'];

export const reviewKeys = {
  all: ['review'] as const,
  comments: (orgSlug: string, projectId: string, candidateId: string) =>
    [
      ...reviewKeys.all,
      'comments',
      orgSlug,
      projectId,
      candidateId,
    ] as const,
  annotations: (
    orgSlug: string,
    projectId: string,
    candidateId: string,
    frame: number
  ) =>
    [
      ...reviewKeys.all,
      'annotations',
      orgSlug,
      projectId,
      candidateId,
      frame,
    ] as const,
};

async function fetchComments(
  orgSlug: string,
  projectId: string,
  candidateId: string
): Promise<CommentOut[]> {
  const { data } = await client.GET(
    '/api/v1/orgs/{org_slug}/projects/{project_id}/candidates/{candidate_id}/comments',
    {
      params: {
        path: {
          org_slug: orgSlug,
          project_id: projectId,
          candidate_id: candidateId,
        },
      },
    }
  );
  if (!data) throw new Error('useComments: empty response');
  return data;
}

export function useComments(
  orgSlug: string | undefined,
  projectId: string | undefined,
  candidateId: string | undefined
): UseQueryResult<CommentOut[]> {
  return useQuery({
    queryKey:
      orgSlug && projectId && candidateId
        ? reviewKeys.comments(orgSlug, projectId, candidateId)
        : reviewKeys.all,
    queryFn:
      orgSlug && projectId && candidateId
        ? () => fetchComments(orgSlug, projectId, candidateId)
        : skipToken,
  });
}

async function fetchAnnotations(
  orgSlug: string,
  projectId: string,
  candidateId: string,
  frame: number
): Promise<AnnotationOut[]> {
  const { data } = await client.GET(
    '/api/v1/orgs/{org_slug}/projects/{project_id}/candidates/{candidate_id}/annotations',
    {
      params: {
        path: {
          org_slug: orgSlug,
          project_id: projectId,
          candidate_id: candidateId,
        },
        query: { frame },
      },
    }
  );
  if (!data) throw new Error('useAnnotations: empty response');
  return data;
}

export function useAnnotations(
  orgSlug: string | undefined,
  projectId: string | undefined,
  candidateId: string | undefined,
  frame: number
): UseQueryResult<AnnotationOut[]> {
  return useQuery({
    queryKey:
      orgSlug && projectId && candidateId
        ? reviewKeys.annotations(orgSlug, projectId, candidateId, frame)
        : reviewKeys.all,
    queryFn:
      orgSlug && projectId && candidateId
        ? () => fetchAnnotations(orgSlug, projectId, candidateId, frame)
        : skipToken,
  });
}
